import { useState } from 'react'
import { useFinancialPlanStore, Account, RealEstate } from '../../store/financialPlanStore'
import { Plus, Trash2, Wallet, Home, DollarSign } from 'lucide-react'

export function AccountsStep() {
  const {
    currentPlan,
    addAccount,
    updateAccount,
    removeAccount,
    addRealEstate,
    removeRealEstate,
  } = useFinancialPlanStore()
  
  const [showAccountForm, setShowAccountForm] = useState(false)
  const [showRealEstateForm, setShowRealEstateForm] = useState(false)
  
  const [newAccount, setNewAccount] = useState<Omit<Account, 'id'>>({
    name: '',
    type: 'Traditional IRA',
    balance: 0,
    institution: '',
    annualContribution: 0,
    stocksPercent: 60,
    bondsPercent: 30,
    cashPercent: 10,
  })
  
  const [newProperty, setNewProperty] = useState<Omit<RealEstate, 'id'>>({
    type: 'Primary Residence',
    value: 0,
    mortgageBalance: 0,
    monthlyPayment: 0,
  })
  
  if (!currentPlan) return null
  
  const totalAccounts = currentPlan.accounts.reduce((sum, acc) => sum + acc.balance, 0)
  const totalRealEstate = currentPlan.realEstate.reduce((sum, prop) => sum + prop.value, 0)
  const totalNetWorth = totalAccounts + totalRealEstate -
    currentPlan.realEstate.reduce((sum, prop) => sum + prop.mortgageBalance, 0)
  
  const handleAddAccount = () => {
    if (newAccount.name && newAccount.balance >= 0) {
      addAccount(newAccount)
      setNewAccount({
        name: '',
        type: 'Traditional IRA',
        balance: 0,
        institution: '',
        annualContribution: 0,
        stocksPercent: 60,
        bondsPercent: 30,
        cashPercent: 10,
      })
      setShowAccountForm(false)
    }
  }
  
  const handleAddProperty = () => {
    if (newProperty.value > 0) {
      addRealEstate(newProperty)
      setNewProperty({
        type: 'Primary Residence',
        value: 0,
        mortgageBalance: 0,
        monthlyPayment: 0,
      })
      setShowRealEstateForm(false)
    }
  }
  
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }
  
  return (
    <div className="space-y-8">
      {/* Summary Card */}
      <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg p-6">
        <h3 className="text-sm font-medium text-primary-900 mb-4">Net Worth Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-primary-700">Investment Accounts</p>
            <p className="text-2xl font-bold text-primary-900">{formatCurrency(totalAccounts)}</p>
          </div>
          <div>
            <p className="text-xs text-primary-700">Real Estate Value</p>
            <p className="text-2xl font-bold text-primary-900">{formatCurrency(totalRealEstate)}</p>
          </div>
          <div>
            <p className="text-xs text-primary-700">Total Net Worth</p>
            <p className="text-2xl font-bold text-primary-900">{formatCurrency(totalNetWorth)}</p>
          </div>
        </div>
      </div>
      
      {/* Investment Accounts Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center">
            <Wallet className="h-5 w-5 mr-2 text-primary-600" />
            Investment Accounts
          </h3>
          {!showAccountForm && (
            <button
              onClick={() => setShowAccountForm(true)}
              className="btn-primary text-sm flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Account
            </button>
          )}
        </div>
        
        {/* Existing Accounts */}
        <div className="space-y-3">
          {currentPlan.accounts.map(account => (
            <div key={account.id} className="card border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-medium">{account.name}</h4>
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      {account.type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{account.institution}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Balance</p>
                      <p className="font-medium">{formatCurrency(account.balance)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Annual Contribution</p>
                      <p className="font-medium">{formatCurrency(account.annualContribution)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Allocation</p>
                      <p className="text-sm">{account.stocksPercent}% / {account.bondsPercent}% / {account.cashPercent}%</p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeAccount(account.id)}
                  className="text-red-600 hover:text-red-700 ml-4"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Add Account Form */}
        {showAccountForm && (
          <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50 mt-3">
            <h4 className="font-medium mb-4">Add Investment Account</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Account Name *</label>
                  <input
                    type="text"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                    className="input-field"
                    placeholder="e.g., Fidelity 401k"
                  />
                </div>
                <div>
                  <label className="label">Account Type *</label>
                  <select
                    value={newAccount.type}
                    onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value as Account['type'] })}
                    className="input-field"
                  >
                    <option value="Traditional IRA">Traditional IRA</option>
                    <option value="Roth IRA">Roth IRA</option>
                    <option value="401k">401(k)</option>
                    <option value="Roth 401k">Roth 401(k)</option>
                    <option value="Taxable Brokerage">Taxable Brokerage</option>
                    <option value="HSA">HSA</option>
                    <option value="529">529 College Savings</option>
                    <option value="Cash">Cash/Savings</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Current Balance *</label>
                  <input
                    type="number"
                    value={newAccount.balance}
                    onChange={(e) => setNewAccount({ ...newAccount, balance: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    min="0"
                    step="1000"
                  />
                </div>
                <div>
                  <label className="label">Institution</label>
                  <input
                    type="text"
                    value={newAccount.institution}
                    onChange={(e) => setNewAccount({ ...newAccount, institution: e.target.value })}
                    className="input-field"
                    placeholder="e.g., Vanguard, Fidelity"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Annual Contribution</label>
                  <input
                    type="number"
                    value={newAccount.annualContribution}
                    onChange={(e) => setNewAccount({ ...newAccount, annualContribution: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    min="0"
                  />
                </div>
                <div>
                  <label className="label">Employer Match (if applicable)</label>
                  <input
                    type="number"
                    value={newAccount.employerMatch || ''}
                    onChange={(e) => setNewAccount({ ...newAccount, employerMatch: parseFloat(e.target.value) || undefined })}
                    className="input-field"
                    min="0"
                  />
                </div>
              </div>
              
              <div>
                <label className="label">Asset Allocation</label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-600">Stocks %</label>
                    <input
                      type="number"
                      value={newAccount.stocksPercent}
                      onChange={(e) => setNewAccount({ ...newAccount, stocksPercent: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Bonds %</label>
                    <input
                      type="number"
                      value={newAccount.bondsPercent}
                      onChange={(e) => setNewAccount({ ...newAccount, bondsPercent: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Cash %</label>
                    <input
                      type="number"
                      value={newAccount.cashPercent}
                      onChange={(e) => setNewAccount({ ...newAccount, cashPercent: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Total must equal 100%</p>
              </div>
              
              <div className="flex space-x-3">
                <button onClick={handleAddAccount} className="btn-primary">Add Account</button>
                <button onClick={() => setShowAccountForm(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        {currentPlan.accounts.length === 0 && !showAccountForm && (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
            <Wallet className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>No accounts added yet</p>
          </div>
        )}
      </div>
      
      {/* Real Estate Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center">
            <Home className="h-5 w-5 mr-2 text-primary-600" />
            Real Estate
          </h3>
          {!showRealEstateForm && (
            <button
              onClick={() => setShowRealEstateForm(true)}
              className="btn-primary text-sm flex items-center"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Property
            </button>
          )}
        </div>
        
        {/* Existing Properties */}
        <div className="space-y-3">
          {currentPlan.realEstate.map(property => (
            <div key={property.id} className="card border border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-medium">{property.type}</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Current Value</p>
                      <p className="font-medium">{formatCurrency(property.value)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Mortgage Balance</p>
                      <p className="font-medium">{formatCurrency(property.mortgageBalance)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Monthly Payment</p>
                      <p className="font-medium">{formatCurrency(property.monthlyPayment)}</p>
                    </div>
                    {property.rentalIncome && (
                      <div>
                        <p className="text-xs text-gray-500">Rental Income</p>
                        <p className="font-medium">{formatCurrency(property.rentalIncome)}/mo</p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeRealEstate(property.id)}
                  className="text-red-600 hover:text-red-700 ml-4"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Add Property Form */}
        {showRealEstateForm && (
          <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50 mt-3">
            <h4 className="font-medium mb-4">Add Real Estate Property</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Property Type *</label>
                  <select
                    value={newProperty.type}
                    onChange={(e) => setNewProperty({ ...newProperty, type: e.target.value as RealEstate['type'] })}
                    className="input-field"
                  >
                    <option value="Primary Residence">Primary Residence</option>
                    <option value="Rental Property">Rental Property</option>
                    <option value="Vacation Home">Vacation Home</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="label">Current Value *</label>
                  <input
                    type="number"
                    value={newProperty.value}
                    onChange={(e) => setNewProperty({ ...newProperty, value: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    min="0"
                    step="10000"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Mortgage Balance</label>
                  <input
                    type="number"
                    value={newProperty.mortgageBalance}
                    onChange={(e) => setNewProperty({ ...newProperty, mortgageBalance: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    min="0"
                  />
                </div>
                <div>
                  <label className="label">Monthly Payment</label>
                  <input
                    type="number"
                    value={newProperty.monthlyPayment}
                    onChange={(e) => setNewProperty({ ...newProperty, monthlyPayment: parseFloat(e.target.value) || 0 })}
                    className="input-field"
                    min="0"
                  />
                </div>
              </div>
              
              {newProperty.type === 'Rental Property' && (
                <div>
                  <label className="label">Monthly Rental Income</label>
                  <input
                    type="number"
                    value={newProperty.rentalIncome || ''}
                    onChange={(e) => setNewProperty({ ...newProperty, rentalIncome: parseFloat(e.target.value) || undefined })}
                    className="input-field"
                    min="0"
                  />
                </div>
              )}
              
              <div className="flex space-x-3">
                <button onClick={handleAddProperty} className="btn-primary">Add Property</button>
                <button onClick={() => setShowRealEstateForm(false)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        )}
        
        {currentPlan.realEstate.length === 0 && !showRealEstateForm && (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
            <Home className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>No real estate added yet</p>
            <p className="text-sm mt-1">Optional - add if you own property</p>
          </div>
        )}
      </div>
    </div>
  )
}
